import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import SwapCityButton from "./SwapCityButton"
import CityInput from "./CityInput"
import { useDispatch, useSelector } from "react-redux"
import {
  selectAviaSearchTerminals,
  selectAviaTerminalById,
  swapAviaTerminals,
} from "../../../../../app/reducers/book/aviaSearchSlice"
import SearchSuggestionsModal from "./SearchSuggestionsModal"
import PlaneImg from "../../../../../assets/avia/search/plane.png"
import { motion, AnimatePresence } from "framer-motion"
import { cn, getKeyboardLayoutQueryVariants, textMatchesQueryVariants } from "../../../../../lib"
import { useClickOutside } from "../../hooks"
import { findTerminalByIdV2 } from "../../utils/filters/serch"

const TO_FIELD = "to"
const FROM_FIELD = "from"
const RECENT_SEARCHES_KEY = "avia_recent_searches"
const MAX_RECENT_SEARCHES = 3

const getRecentSearches = () => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error("Error reading recent searches:", error)
    return []
  }
}

const saveRecentSearch = (airport, field) => {
  try {
    const recentSearches = getRecentSearches()
    const newSearch = {
      ...airport,
      searchField: field,
      timestamp: new Date().toISOString(),
    }
    const filtered = recentSearches.filter(
      (search) => !(search.id === newSearch.id && search.searchField === newSearch.searchField)
    )
    const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
    return updated
  } catch (error) {
    console.error("Error saving recent search:", error)
    return []
  }
}

const clearRecentSearches = () => {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY)
    return []
  } catch (error) {
    console.error("Error clearing recent searches:", error)
    return []
  }
}

const MainSearchForm = ({
  defaultDeparture,
  defaultArrival,
  departureTerminals,
  arrivalTerminals,
  selectedDeparture,
  selectedArrival,
  fetchTerminals,
  setShouldShowSidebar,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const terminals = useSelector(selectAviaSearchTerminals)

  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [fromQuery, setFromQuery] = useState("")
  const [toQuery, setToQuery] = useState("")
  const [activeField, setActiveField] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [recentSearches, setRecentSearches] = useState([])

  const modalRef = useRef(null)

  const displayDeparture = selectedDeparture
  const displayArrival = selectedArrival
  const displayDepartureTerminals = departureTerminals
  const displayArrivalTerminals = arrivalTerminals

  useEffect(() => {
    if (!to) setTo(defaultArrival?.city ?? "")
    if (!from) setFrom(defaultDeparture?.city ?? "")
  }, [defaultDeparture, defaultArrival])

  useClickOutside(modalRef, () => {
    setToQuery("")
    setFromQuery("")
    setShowModal(false)
  })

  const handleSwapCities = () => {
    setShouldShowSidebar(false)
    const tmp = from
    setFrom(to)
    setTo(tmp)
    dispatch(swapAviaTerminals()) // оставляем для обновления сторе
  }

  const handleInputFocus = (field) => {
    setActiveField(field)
    setShowModal(true)
    if (field === FROM_FIELD) setFromQuery(from)
    else setToQuery(to)
  }

  const handleInputChange = (field, value) => {
    const isFrom = field === FROM_FIELD
    if (isFrom) {
      setFrom(value)
      setFromQuery(value)
    } else {
      setTo(value)
      setToQuery(value)
    }
    setActiveField(field)
    setShowModal(true)
  }

  const filterSuggestions = (query, suggestions) => {
    if (!query.trim()) {
      return [...suggestions].sort((a, b) => {
        const aSelected = (a.terminals ?? []).some((t) => t.is_selected)
        const bSelected = (b.terminals ?? []).some((t) => t.is_selected)
        return bSelected - aSelected
      })
    }

    const queryVariants = getKeyboardLayoutQueryVariants(query)
    return suggestions
      .map((section) => {
        const match =
          textMatchesQueryVariants(section.city, queryVariants) ||
          textMatchesQueryVariants(section.country, queryVariants) ||
          textMatchesQueryVariants(section.iata, queryVariants)

        const filteredTerminals = (section.terminals ?? []).filter(
          (t) =>
            textMatchesQueryVariants(t.city, queryVariants) ||
            textMatchesQueryVariants(t.name, queryVariants) ||
            textMatchesQueryVariants(t.iata, queryVariants)
        )

        return {
          ...section,
          terminals: match ? section.terminals ?? [] : filteredTerminals,
        }
      })
      .filter((section) => section.terminals.length > 0)
      .sort((a, b) => {
        const aSelected = a.terminals.some((t) => t.is_selected)
        const bSelected = b.terminals.some((t) => t.is_selected)
        return bSelected - aSelected
      })
  }

  const { query, sourceData } = useMemo(() => {
    if (activeField === FROM_FIELD) return { query: fromQuery, sourceData: displayDepartureTerminals ?? [] }
    if (activeField === TO_FIELD) return { query: toQuery, sourceData: displayArrivalTerminals ?? [] }
    return { query: "", sourceData: [] }
  }, [activeField, fromQuery, toQuery, departureTerminals, arrivalTerminals])

  const filteredSuggestions = useMemo(() => filterSuggestions(query, sourceData), [query, sourceData])

  const handleTerminal = async (terminalId, field) => {
    const type = field === FROM_FIELD ? "departure" : "arrival"
    dispatch(selectAviaTerminalById({ terminalId, type }))
    if (field === FROM_FIELD) await fetchTerminals({ terminalId })

    const terminalData = findTerminalByIdV2(terminalId, sourceData)
    if (terminalData) {
      const updated = saveRecentSearch(terminalData, field)
      setRecentSearches(updated)
    }

    if (field === FROM_FIELD) setFromQuery("")
    else setToQuery("")
  }

  const handleRecentSearchSelect = (recentSearch) => {
    handleTerminal(recentSearch.id, recentSearch.searchField)
    setShowModal(false)
  }

  const handleClearRecentSearches = () => setRecentSearches(clearRecentSearches())

  const handleQueryChange = (value) => {
    if (activeField === FROM_FIELD) setFromQuery(value)
    else setToQuery(value)
  }

  return (
    <div className="flex bg-white rounded-md shadow-md items-center h-[53px] p-2 w-full min-w-[450px] overflow-visible relative">
      <div className="flex items-center w-full gap-4">
        <FieldWrapper t={t}>
          <CityField
            planeIcon={PlaneImg}
            label={t("admin.departure_from_v3")}
            terminals={displayDeparture}
            onClear={(id) => handleTerminal(id, FROM_FIELD)}
            placeholder={t("admin.city_of_departure")}
            rotated
            handleInputFocus={() => handleInputFocus(FROM_FIELD)}
            handleInputChange={(value) => handleInputChange(FROM_FIELD, value)}
            valueInput={fromQuery}
          />
        </FieldWrapper>

        <div className="shrink-0">
          <SwapCityButton onClick={handleSwapCities} />
        </div>

        <FieldWrapper isFrom={false} t={t}>
          <CityField
            label={t("filter.to_where")}
            terminals={displayArrival}
            onClear={(id) => handleTerminal(id, TO_FIELD)}
            placeholder={t("admin.city_of_arrival")}
            handleInputFocus={() => handleInputFocus(TO_FIELD)}
            handleInputChange={(value) => handleInputChange(TO_FIELD, value)}
            valueInput={toQuery}
          />
        </FieldWrapper>
      </div>

      {showModal && (
        <AnimatePresence>
          <SearchSuggestionsModal
            ref={modalRef}
            filteredSuggestions={filteredSuggestions}
            selectAirport={handleTerminal}
            activeField={activeField}
            onQueryChange={handleQueryChange}
            initialQuery={activeField === FROM_FIELD ? fromQuery : toQuery}
            fetchTerminals={fetchTerminals}
            selectedDeparture={displayDeparture}
            selectedArrival={displayArrival}
            onClearTerminals={(id) => handleTerminal(id, activeField)}
          />
        </AnimatePresence>
      )}
    </div>
  )
}

export default MainSearchForm

// ------------------ Supporting Components ------------------

const CityField = ({ terminals, onClear, placeholder, handleInputFocus, handleInputChange, valueInput }) => {
  const containerRef = useRef(null)
  const terminalsRef = useRef(null)

  const calculateOffset = () => {
    if (!containerRef.current || !terminalsRef.current) return
    const containerWidth = containerRef.current.offsetWidth
    const terminalsWidth = terminalsRef.current.scrollWidth
    const minInputWidth = containerWidth * 0.1
    const available = containerWidth - minInputWidth
    terminalsRef.current.scrollLeft = terminalsWidth > available ? terminalsWidth - available : 0
  }

  useEffect(() => {
    window.addEventListener("resize", calculateOffset)
    return () => window.removeEventListener("resize", calculateOffset)
  }, [])

  useEffect(() => {
    const timeout = setTimeout(calculateOffset, 310)
    return () => clearTimeout(timeout)
  }, [terminals])

  return (
    <div ref={containerRef} className="flex items-center w-full min-w-0">
      <div ref={terminalsRef} className="overflow-hidden flex items-center min-w-0 max-w-[90%] flex-shrink-0" style={{ scrollBehavior: "smooth" }}>
        <div className="flex items-center gap-1 whitespace-nowrap">
          <AnimatePresence mode="popLayout" onExitComplete={calculateOffset}>
            {terminals.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, x: -20, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.9 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <CityInput value={item.city} onClear={() => onClear(item.id)} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      <input
        type="text"
        className="flex-1 min-w-[10%] pl-2"
        placeholder={placeholder}
        onFocus={handleInputFocus}
        value={valueInput}
        onChange={(e) => handleInputChange(e.target.value)}
      />
    </div>
  )
}

const FieldWrapper = ({ children, isFrom = true, t }) => (
  <div className="flex items-center gap-1 flex-1 basis-0 min-w-0 z-10 bg-white relative h-[53px] pt-3">
    <span className="absolute top-[6px] left-0 text-[10px] font-medium text-text-secondary whitespace-nowrap bg-white px-0.5 leading-none z-10 pointer-events-none">
      {t(isFrom ? "admin.departure_from_v3" : "filter.to_where")}
    </span>

    <div className="flex items-center gap-1 shrink-0">
      <img src={PlaneImg} alt="plane icon" className={cn("w-[22px] h-[22px] shrink-0", !isFrom && "rotate-90")} />
      <div className="w-px h-4 bg-light-gray/30 shrink-0"></div>
    </div>

    <div className="flex-1 min-w-0">{children}</div>
  </div>
)